import fetch             from '../../../utils/browser-fetch';
import busyActionFactory from '../../../busy';

/*******************************************************************************
 * Initial state
 */

const initialState = {
    busyFetchingAccountHistory: false,
    pagedActivityHistoryData: [],
    currentPageActivityHistoryData: [],
    currentPage: 0,
    totalPages: 0,
    actualData: [],
    sortedColumn: null,
    sortedColumnOrder: 'asc',
    startDate: null,
    endDate: null,
    unavailable: false
};


/*******************************************************************************
 * Non-async action types
 */
export const BUSY_FETCHING_ACTIVITY_HISTORY = 'BUSY_FETCHING_ACTIVITY_HISTORY';
export const ACTIVITY_HISTORY_DATA_RECIEVED = 'ACTIVITY_HISTORY_DATA_RECIEVED';
export const ACTIVITY_HISTORY_DATA_UNAVAILABLE = 'ACTIVITY_HISTORY_DATA_UNAVAILABLE';
export const ACTIVITY_HISTORY_DATES = 'ACTIVITY_HISTORY_DATES';
export const ACTIVITY_HISTORY_SORT_ON_COLUMN = 'ACTIVITY_HISTORY_SORT_ON_COLUMN';
export const ACTIVITY_HISTORY_NAVIGATE_TO_PAGE = 'ACTIVITY_HISTORY_NAVIGATE_TO_PAGE';

/*******************************************************************************
 * Action creators
 */
export function sortByColumn( sortColumn ) {
    return {
        type: ACTIVITY_HISTORY_SORT_ON_COLUMN,
        sortColumn
    };
}

export function fetchDates( startDate, endDate ) {
    return {
        type: ACTIVITY_HISTORY_DATES,
        startDate,
        endDate
    };
}

export function navigateToPage( page ) {
    return {
        type: ACTIVITY_HISTORY_NAVIGATE_TO_PAGE,
        page
    };
}

export function setActivityHistoryData( data ) {
    return {
        type: ACTIVITY_HISTORY_DATA_RECIEVED,
        accountNumber: data.accountNumber,
        accountHistory: data.accountHistoryList
    };
}

export function setActivityHistoryUnavailable() {
    return {
        type: ACTIVITY_HISTORY_DATA_UNAVAILABLE
    };
}

const busyFetchingAccountHistory = busyActionFactory( BUSY_FETCHING_ACTIVITY_HISTORY );
export function fetchActivityHistoryData( username, locale ) {
    return ( dispatch ) => {
        dispatch( busyFetchingAccountHistory( true ) );
        return fetch( `/api/v1.0/user/account/statement?username=${ username }&locale=${ encodeURIComponent( locale ) }` )
            .then( response => {
                dispatch( setActivityHistoryData( response ) );
                dispatch( busyFetchingAccountHistory( false ) );
            } ).catch( () => {
                dispatch( setActivityHistoryUnavailable() );
                dispatch( busyFetchingAccountHistory( false ) );
            } );
    };
}

/*******************************************************************************
 * Reducer
 */

export default function activityHistoryData( state = initialState, action ) {
    let split = null;

    switch ( action.type ) {
        case BUSY_FETCHING_ACTIVITY_HISTORY:
            return {
                ...state,
                busyFetchingAccountHistory: action.busy
            };

        case ACTIVITY_HISTORY_DATA_RECIEVED:
            const actualData = [ ...action.accountHistory ];

            //convert date strings to dates here to avoid dealing with Safari's crap parsing.
            actualData.forEach( ( item ) => {
                item.date = new Date( item.date.replace( /([+-]\d\d)(\d\d)$/, '$1:$2' ) );
            } );

            split = splitData( sortData( filterData( actualData, state.startDate, state.endDate ), state.sortedColumn, state.sortedColumnOrder ) );

            return {
                ...state,
                pagedActivityHistoryData: split.pagedData,
                currentPageActivityHistoryData: split.pagedData[ 0 ],
                currentPage: 1,
                totalPages: split.totalPages,
                actualData: actualData,
                accountNumber: action.accountNumber,
                unavailable: false
            };

        case ACTIVITY_HISTORY_DATA_UNAVAILABLE:
            return {
                ...state,
                unavailable: true
            };

        case ACTIVITY_HISTORY_SORT_ON_COLUMN:
            const sortOrder = state.sortedColumn === action.sortColumn
                ? ( state.sortedColumnOrder === 'asc' ? 'desc' : 'asc' ) : 'desc';

            split = splitData( sortData( filterData( state.actualData, state.startDate, state.endDate ), action.sortColumn, sortOrder ) );

            return {
                ...state,
                pagedActivityHistoryData: split.pagedData,
                currentPageActivityHistoryData: split.pagedData[ state.currentPage - 1 ],
                sortedColumn: action.sortColumn,
                sortedColumnOrder: sortOrder
            };

        case ACTIVITY_HISTORY_NAVIGATE_TO_PAGE:
            return {
                ...state,
                currentPageActivityHistoryData: state.pagedActivityHistoryData[ action.page - 1 ],
                currentPage: action.page
            };

        case ACTIVITY_HISTORY_DATES:

            split = splitData( sortData( filterData( state.actualData, action.startDate, action.endDate ), state.sortedColumn, state.sortedColumnOrder ) );

            return {
                ...state,
                startDate: action.startDate,
                endDate: action.endDate,
                pagedActivityHistoryData: split.pagedData,
                currentPage: 1,
                currentPageActivityHistoryData: split.pagedData[ 0 ],
                totalPages: split.totalPages
            };

        default:
            return state;
    }
}

function splitData( rows ) {
    rows = [ ...rows ]; //copy this so as not to mutate the parameter passed in.

    const pagedData = [];
    const floorTotalEntries = Math.floor( rows.length / 15 );
    const totalPages = ( rows.length / 15 ) > floorTotalEntries ?
        floorTotalEntries + 1 : floorTotalEntries;

    for( let pageIndex = 0; pageIndex < totalPages; pageIndex++ ) {
        const remainingEntries = rows.length;
        if( remainingEntries >= 15 ) {
            pagedData[ pageIndex ] = rows.splice( 0, 15 );
        } else {
            pagedData[ pageIndex ] = rows.splice( 0, remainingEntries );
        }
    }

    return {
        pagedData: pagedData,
        totalPages: totalPages
    };
}

function filterData( rows, startDate, endDate ) {
    let startDateValue = null;
    if ( startDate ) {
        startDateValue = new Date( startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0 ).valueOf();
    }

    let endDateValue = null;
    if ( endDate ) {
        endDateValue = new Date( endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999 ).valueOf();
    } else if ( startDate ) {
        endDateValue = new Date( startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 23, 59, 59, 999 ).valueOf();
    }

    return rows.filter( function ( item ) {
        const dateValue = new Date( item.date ).valueOf();

        if ( startDateValue && endDateValue ) {
            return dateValue >= startDateValue && dateValue <= endDateValue;
        } else {
            return true;
        }
    } );
}

function sortData( rows, sortOn, sortBy ) {
    rows = [ ...rows ]; //copy this so as not to mutate the paramter passed in.

    const doSortBy = {
        asc: ( x, y ) => +( x > y ) || +( x === y ) - 1,
        desc: ( x, y ) => +( x < y ) || +( x === y ) - 1
    };

    const doSortOn = {
        date: function() {
            rows.sort( ( a, b ) => {
                return doSortBy[ sortBy ]( a.date.valueOf(), b.date.valueOf() );
            } );
        },
        description: function() {
            rows.sort( ( a, b ) => {
                return doSortBy[ sortBy ]( a.description.toLowerCase(), b.description.toLowerCase() );
            } );
        },
        amount: function() {
            rows.sort( ( a, b ) => {
                return doSortBy[ sortBy ]( parseInt( a.amountOrAward, 10 ), parseInt( b.amountOrAward, 10 ) );
            } );
        }
    };

    if ( doSortOn[ sortOn ] ) {
        doSortOn[ sortOn ]();
    }

    return rows;
}


